import { useState } from 'react';
import { isError, isNil } from 'lodash';
import { IEditSmartReplyQuery, useEditSmartReplyLazyQuery } from '@copilot/data/graphql/_generated';
import {
    CUSTOM_PROMPT_KEY,
    EditSmartReplyHook,
    WriteSmartReplyErrors,
} from '@copilot/common/hooks/smartReply/smartReplyTypes';
import { isSmartReply, isSmartReplyError } from '@copilot/common/hooks/smartReply/smartReplyUtils';
import { useChatGptFeature } from './useChatGptFeature';
import { ErrorMap } from '../smartReplyConstants';
import { CommunicationStylesAnalysis } from '@copilot/common/types/communicationStyles';
import { useFeatureFlags } from '../../useFeatureFlags/useFeatureFlags';
/**
 * [Smart] Hook for editing a message with Smart Reply
 * @param orgId
 * @param memberId
 * @param contactId
 */
export function useEditSmartReply(
    communicationStyles?: CommunicationStylesAnalysis
): EditSmartReplyHook {
    // If ChatGPT is not enabled, return immediately
    const { isChatGptFeatureEnabled, handleChatGptFeatureFlagOff } = useChatGptFeature();
    if (!isChatGptFeatureEnabled) {
        return [
            { loading: false, data: undefined, error: new Error(WriteSmartReplyErrors.disabled) },
            { editSmartReply: handleChatGptFeatureFlagOff },
        ];
    }

    const [editSmartReplyQuery, { data }] = useEditSmartReplyLazyQuery({
        notifyOnNetworkStatusChange: true,
    });
    const [error, setError] = useState<Error | undefined>(undefined);
    const [loading, setLoading] = useState(false);

    const { isNewCommStyleSmartReplyEnabled } = useFeatureFlags();

    /**
     * Callback for Editing a Smart Reply given a prompt
     * @param messageToBeEdited
     * @param promptId
     * @param message
     */
    async function editSmartReply(
        messageToBeEdited: string,
        promptId: string,
        message?: string,
        includePersonalizedInsights?: boolean
    ) {
        return editSmartReplyQuery({
            variables: {
                input: {
                    message: messageToBeEdited,
                    recipientCommunicationStyles:
                        includePersonalizedInsights && isNewCommStyleSmartReplyEnabled
                            ? communicationStyles?.styles.map((style) => style.style)
                            : undefined,
                    prompt: {
                        // TODO - COPILOT-6513: Update to use promptId with no conditional.
                        promptId: isNil(message) ? promptId : CUSTOM_PROMPT_KEY,
                        message,
                    },
                },
            },
        });
    }

    const callbacks = { editSmartReply: prepFetch(editSmartReply) };

    if (loading) return [{ loading, data: undefined, error: undefined }, callbacks];

    if (error) return [{ loading: false, data: undefined, error: error }, callbacks];

    // Return the message if it can be parsed
    if (!isNil(data) && isSmartReply(data?.editSmartReply)) {
        return [{ loading, error: undefined, data: data.editSmartReply.message }, callbacks];
    }

    // Return an error if no previous condition was met
    return [
        { loading, data: undefined, error: new Error(WriteSmartReplyErrors.unexpectedFormat) },
        callbacks,
    ];

    /**
     * Wrap fetches with state management
     * @param fetchFn The function we want to wrap
     */
    function prepFetch<T extends (...args: any[]) => Promise<{ data?: IEditSmartReplyQuery }>>(
        fetchFn: T
    ) {
        return async function (...args: Parameters<T>) {
            setLoading(true);
            setError(undefined);
            try {
                const result = await fetchFn(...args);
                if (!isNil(result?.data) && isSmartReplyError(result?.data.editSmartReply)) {
                    const errorMessage =
                        ErrorMap[result.data.editSmartReply.type] ??
                        WriteSmartReplyErrors.generation;
                    throw new Error(errorMessage);
                }
            } catch (err: unknown) {
                if (isError(err)) setError(err);
                else throw err;
            } finally {
                setLoading(false);
            }
        };
    }
}
